import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'

import { F0Alert, F0Box, F0Form, F0Heading, F0Text, f0FormField } from '@factorialco/f0-react'
import type { CustomFieldRenderProps, F0FormRef } from '@factorialco/f0-react'
import { NumberInput } from '@factorialco/f0-react/dist/experimental'
import {
  TrainingsTrainingClassPaymentStatusEnum,
  UpdateCostsClassDetailDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import { toCents } from 'modules/core/lib/formatCurrencyUtils'
import { amountWithCurrency } from 'modules/finance/lib/budgets'
import i18n from 'modules/core/lib/i18n'

import type { TrainingBudgetMovement } from 'modules/trainings/components/Budgets/TrainingResourceDataCollection/index'

interface Props {
  item: TrainingBudgetMovement
  locale: string
  groupUrl: string
  canEdit: boolean
  formRef: React.MutableRefObject<F0FormRef | null>
  budgetId: string
  onSaved?: (paymentStatus: string | undefined) => void
}

const MAX_COST_VALUE = 999999

const PAYMENT_STATUS = {
  PAID: 'paid',
  PENDING: 'pending',
} as const satisfies Record<string, TrainingsTrainingClassPaymentStatusEnum>

const MOVEMENT_STATUS = {
  SPENT: 'spent',
  PENDING: 'pending',
} as const

/** Maps budget movement status to training class payment status */
const mapMovementStatusToPaymentStatus = (
  status: string | null | undefined
): TrainingsTrainingClassPaymentStatusEnum | undefined => {
  if (status === MOVEMENT_STATUS.SPENT) return PAYMENT_STATUS.PAID
  if (status === MOVEMENT_STATUS.PENDING) return PAYMENT_STATUS.PENDING
  return undefined
}

export const CostTab = ({ item, locale, groupUrl, canEdit, formRef, budgetId, onSaved }: Props) => {
  const navigate = useNavigate()
  const resource = item.budgetedResource
  const isCurrencyConverted = item.sourceCurrency !== item.targetCurrency
  const displayCurrency = isCurrencyConverted ? item.targetCurrency : (resource.currency ?? item.sourceCurrency)
  const sourceCurrency = resource.currency ?? item.sourceCurrency
  const exchangeRate = item.exchangeRate ?? 1

  const [savedGrossCost, setSavedGrossCost] = useState<number | null>(null)

  const [updateCosts] = useMutation(UpdateCostsClassDetailDocument, {
    extractorFn: (d) => d.trainingsMutations.updateTrainingsTrainingClass,
  })

  const schema = useMemo(
    () =>
      z.object({
        paymentStatus: f0FormField(z.enum([PAYMENT_STATUS.PENDING, PAYMENT_STATUS.PAID]).optional(), {
          label: i18n.t('trainings.classes.payment_status.label'),
          options: [
            {
              value: PAYMENT_STATUS.PENDING,
              label: i18n.t('trainings.classes.payment_status.pending'),
            },
            {
              value: PAYMENT_STATUS.PAID,
              label: i18n.t('trainings.classes.payment_status.paid'),
            },
          ],
        }),
        cost: f0FormField(z.number().min(0).max(MAX_COST_VALUE).nullable(), {
          label: i18n.t('budgets.detail_training_budget.sidepanel.direct_cost_desc'),
          fieldType: 'custom',
          render: ({ value, onChange }: CustomFieldRenderProps<number | null>) => (
            <NumberInput
              label={i18n.t('budgets.detail_training_budget.sidepanel.direct_cost_desc')}
              locale={i18n.locale}
              units={displayCurrency}
              value={value ?? null}
              onChange={(v: number | null) => onChange(v)}
              maxDecimals={2}
              size="md"
            />
          ),
        }),
        indirectCost: f0FormField(z.number().min(0).max(MAX_COST_VALUE).nullable(), {
          label: i18n.t('budgets.detail_training_budget.sidepanel.indirect_cost_desc'),
          fieldType: 'custom',
          render: ({ value, onChange }: CustomFieldRenderProps<number | null>) => (
            <NumberInput
              label={i18n.t('budgets.detail_training_budget.sidepanel.indirect_cost_desc')}
              locale={i18n.locale}
              units={displayCurrency}
              value={value ?? null}
              onChange={(v: number | null) => onChange(v)}
              maxDecimals={2}
              size="md"
            />
          ),
        }),
        salaryCost: f0FormField(z.number().min(0).max(MAX_COST_VALUE).nullable(), {
          label: i18n.t('budgets.detail_training_budget.sidepanel.salary_cost_desc'),
          fieldType: 'custom',
          render: ({ value, onChange }: CustomFieldRenderProps<number | null>) => (
            <NumberInput
              label={i18n.t('budgets.detail_training_budget.sidepanel.salary_cost_desc')}
              locale={i18n.locale}
              units={displayCurrency}
              value={value ?? null}
              onChange={(v: number | null) => onChange(v)}
              maxDecimals={2}
              size="md"
            />
          ),
        }),
      }),
    [displayCurrency]
  )

  const convertToDisplay = (value: number): number =>
    isCurrencyConverted ? Math.round(value * exchangeRate * 100) / 100 : value

  const convertToSource = (value: number): number =>
    isCurrencyConverted && exchangeRate !== 0
      ? Math.round((value / exchangeRate) * 100) / 100
      : value

  const defaultValues = useMemo(
    () => ({
      paymentStatus: mapMovementStatusToPaymentStatus(item.status) ?? PAYMENT_STATUS.PENDING,
      cost: resource.cost ? convertToDisplay(Number(resource.cost)) : null,
      indirectCost: resource.indirectCost ? convertToDisplay(Number(resource.indirectCost)) : null,
      salaryCost: resource.salaryCost ? convertToDisplay(Number(resource.salaryCost)) : null,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [item.status, resource.cost, resource.indirectCost, resource.salaryCost, exchangeRate]
  )

  const handleSubmit = async (data: z.infer<typeof schema>) => {
    const result = await updateCosts({
      variables: {
        id: resource.id,
        cost: String(convertToSource(data.cost ?? 0)),
        indirectCost: String(convertToSource(data.indirectCost ?? 0)),
        salaryCost: String(convertToSource(data.salaryCost ?? 0)),
        paymentStatus: data.paymentStatus,
        budgetId,
      },
      refetchQueries: [
        'FinanceBudgetDetailTrainingResourceDataCollection',
        'FinanceBudgetDetailRevampPage',
        'FinanceBudgetDetailAmountWidgets',
      ],
      awaitRefetchQueries: true,
    })

    if (!result) return { success: false as const }

    let formErrors: Record<string, string> = {}
    const handled = handleResult(result, (errors) => {
      formErrors = errors
    })

    if (!handled.success) {
      return { success: false as const, errors: formErrors }
    }

    setSavedGrossCost(
      convertToSource(data.cost ?? 0) + convertToSource(data.indirectCost ?? 0) + convertToSource(data.salaryCost ?? 0)
    )
    onSaved?.(data.paymentStatus)

    return { success: true as const }
  }

  const formatAmount = (amount: string | number | null | undefined) => {
    if (amount == null) return '-'
    const cents = toCents(Number(amount))
    const displayCents = isCurrencyConverted ? Math.round(cents * exchangeRate) : cents
    return amountWithCurrency(displayCents, displayCurrency, locale)
  }

  if (!canEdit) {
    return (
      <F0Box display="flex" flexDirection="column" paddingY="lg" gap="lg">
        <F0Box>
          <F0Heading
            content={i18n.t('budgets.detail_training_budget.sidepanel.cost_breakdown_title')}
          />
        </F0Box>
        <F0Box border="default" borderRadius="md" flexDirection="column" borderColor="secondary">
          <CostRow
            title={i18n.t('budgets.detail_training_budget.sidepanel.direct_cost_desc')}
            description={i18n.t('budgets.detail_training_budget.sidepanel.direct_cost_hint')}
            amount={formatAmount(resource.cost)}
          />
          <CostRow
            title={i18n.t('budgets.detail_training_budget.sidepanel.indirect_cost_desc')}
            description={i18n.t('budgets.detail_training_budget.sidepanel.indirect_cost_hint')}
            amount={formatAmount(resource.indirectCost)}
            withDivider
          />
          <CostRow
            title={i18n.t('budgets.detail_training_budget.sidepanel.salary_cost_desc')}
            description={i18n.t('budgets.detail_training_budget.sidepanel.salary_cost_hint')}
            amount={formatAmount(resource.salaryCost)}
            withDivider
          />
        </F0Box>
        <F0Box paddingX="md" borderColor="secondary" borderBottom="default" />
        <F0Box border="default" borderRadius="md" borderColor="secondary">
          <CostRow
            title={i18n.t('budgets.detail_training_budget.sidepanel.total_cost_desc')}
            description={i18n.t('budgets.detail_training_budget.sidepanel.total_cost_hint')}
            amount={formatAmount(resource.grossCost)}
            bold
          />
        </F0Box>
        {isCurrencyConverted && (
          <F0Box>
            <F0Alert
              variant="info"
              title={i18n.t('budgets.detail_training_budget.sidepanel.currency_conversion_note', {
                source: item.sourceCurrency,
                target: item.targetCurrency,
              })}
              description=""
            />
          </F0Box>
        )}
      </F0Box>
    )
  }

  return (
    <F0Box display="flex" flexDirection="column" gap="lg" paddingX="sm">
      <F0Form
        formRef={formRef}
        name="training-budget-cost-edit"
        schema={schema}
        defaultValues={defaultValues}
        submitConfig={{ type: 'default', hideSubmitButton: true }}
        onSubmit={handleSubmit}
      />

      <F0Box
        display="flex"
        flexDirection="row"
        justifyContent="between"
        alignItems="center"
        padding="lg"
        borderRadius="md"
        background="secondary"
      >
        <F0Box flexDirection="column" gap="xs">
          <F0Heading content={i18n.t('budgets.detail_training_budget.sidepanel.total_cost_desc')} />
          <F0Text
            variant="description"
            content={i18n.t('budgets.detail_training_budget.sidepanel.total_cost_hint')}
          />
        </F0Box>
        <F0Heading content={formatAmount(savedGrossCost ?? resource.grossCost)} />
      </F0Box>

      {isCurrencyConverted && (
        <F0Alert
          variant="info"
          title={i18n.t('budgets.detail_training_budget.sidepanel.currency_conversion_note', {
            source: item.sourceCurrency,
            target: item.targetCurrency,
          })}
          description=""
        />
      )}
    </F0Box>
  )
}

const CostRow = ({
  title,
  description,
  amount,
  bold = false,
  withDivider = false,
}: {
  title: string
  description: string
  amount: string
  bold?: boolean
  withDivider?: boolean
}) => (
  <F0Box
    display="flex"
    flexDirection="row"
    justifyContent="between"
    alignItems="center"
    padding="md"
    borderTop={withDivider ? 'default' : undefined}
    borderColor={withDivider ? 'secondary' : undefined}
  >
    <F0Box flexDirection="column" gap="xs">
      <F0Text variant="label" content={title} />
      <F0Text variant="description" content={description} ellipsis />
    </F0Box>
    <F0Text variant={bold ? 'label' : 'body'} content={amount} />
  </F0Box>
)
